import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoIcon from '@mui/icons-material/Info';
import { adminApi } from '../redux/adminApiSlice';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`security-tabpanel-${index}`}
      aria-labelledby={`security-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Security: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [timeframe, setTimeframe] = useState<'day' | 'week' | 'month'>('day');
  const [violationFilters, setViolationFilters] = useState<{
    page: number;
    limit: number;
    violationType?: string;
    severity?: string;
    resolved?: boolean;
    userId?: string;
  }>({
    page: 1,
    limit: 50,
  });
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedViolation, setSelectedViolation] = useState<any>(null);
  const [resolveReason, setResolveReason] = useState('');

  // API queries
  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = adminApi.useGetViolationStatsQuery({ timeframe });
  const { data: violationsData, isLoading: violationsLoading, refetch: refetchViolations } = adminApi.useGetAllViolationsQuery(violationFilters);
  const { data: highRiskUsers, isLoading: highRiskLoading, refetch: refetchHighRisk } = adminApi.useGetHighRiskUsersQuery({ limit: 50 });
  const [resolveViolation] = adminApi.useResolveViolationMutation();
  const [cleanupViolations] = adminApi.useCleanupOldViolationsMutation();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleResolveViolation = async () => {
    if (!selectedViolation || !resolveReason.trim()) return;
    
    try {
      await resolveViolation({
        violationId: selectedViolation._id,
        reason: resolveReason,
      }).unwrap();
      setResolveDialogOpen(false);
      setSelectedViolation(null);
      setResolveReason('');
      refetchViolations();
      refetchStats();
    } catch (error) {
      console.error('Error resolving violation:', error);
    }
  };

  const handleCleanup = async () => {
    if (window.confirm('Are you sure you want to clean up old resolved violations? This action cannot be undone.')) {
      try {
        await cleanupViolations({ daysOld: 90 }).unwrap();
        refetchViolations();
        refetchStats();
      } catch (error) {
        console.error('Error cleaning up violations:', error);
      }
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL': return 'error';
      case 'HIGH': return 'warning';
      case 'MEDIUM': return 'info';
      case 'LOW': return 'default';
      default: return 'default';
    }
  };

  const getViolationTypeLabel = (type: string) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <Box sx={{ width: '100%', height: '100%', overflow: 'auto' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3, pt: 2 }}>
        <Typography variant="h4" gutterBottom>
          <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Security & Violations
        </Typography>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="security tabs">
          <Tab label="Dashboard" />
          <Tab label="Violations" />
          <Tab label="High-Risk Users" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Timeframe</InputLabel>
            <Select
              value={timeframe}
              label="Timeframe"
              onChange={(e) => setTimeframe(e.target.value as 'day' | 'week' | 'month')}
            >
              <MenuItem value="day">Last 24 Hours</MenuItem>
              <MenuItem value="week">Last 7 Days</MenuItem>
              <MenuItem value="month">Last 30 Days</MenuItem>
            </Select>
          </FormControl>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => refetchStats()}
            variant="outlined"
          >
            Refresh
          </Button>
        </Box>

        {statsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : stats?.data ? (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Violations
                  </Typography>
                  <Typography variant="h4">
                    {stats.data.totalViolations || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Unresolved
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {stats.data.unresolvedViolations || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    High Severity
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {(stats.data.bySeverity?.HIGH || 0) + (stats.data.bySeverity?.CRITICAL || 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Affected Users
                  </Typography>
                  <Typography variant="h4">
                    {stats.data.uniqueUsers || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {stats.data.byType && Object.keys(stats.data.byType).length > 0 && (
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Violations by Type
                    </Typography>
                    {Object.entries(stats.data.byType).map(([type, count]: [string, any]) => (
                      <Box key={type} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography>{getViolationTypeLabel(type)}</Typography>
                        <Typography variant="h6">{count}</Typography>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {stats.data.bySeverity && Object.keys(stats.data.bySeverity).length > 0 && (
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Violations by Severity
                    </Typography>
                    {Object.entries(stats.data.bySeverity).map(([severity, count]: [string, any]) => (
                      <Box key={severity} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Chip
                          label={severity}
                          color={getSeverityColor(severity) as any}
                          size="small"
                        />
                        <Typography variant="h6">{count}</Typography>
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        ) : (
          <Alert severity="info">No violation statistics available</Alert>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Violation Type</InputLabel>
            <Select
              value={violationFilters.violationType}
              label="Violation Type"
              onChange={(e) => setViolationFilters({ ...violationFilters, violationType: e.target.value, page: 1 })}
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="IP_DISCREPANCY">IP Discrepancy</MenuItem>
              <MenuItem value="TIMEZONE_MISMATCH">Timezone Mismatch</MenuItem>
              <MenuItem value="IMPOSSIBLE_TRAVEL">Impossible Travel</MenuItem>
              <MenuItem value="GPS_SPOOFING">GPS Spoofing</MenuItem>
              <MenuItem value="VPN_DETECTED">VPN Detected</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Severity</InputLabel>
            <Select
              value={violationFilters.severity}
              label="Severity"
              onChange={(e) => setViolationFilters({ ...violationFilters, severity: e.target.value, page: 1 })}
            >
              <MenuItem value="">All Severities</MenuItem>
              <MenuItem value="LOW">Low</MenuItem>
              <MenuItem value="MEDIUM">Medium</MenuItem>
              <MenuItem value="HIGH">High</MenuItem>
              <MenuItem value="CRITICAL">Critical</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={violationFilters.resolved === undefined ? '' : violationFilters.resolved ? 'true' : 'false'}
              label="Status"
              onChange={(e) => {
                const value = e.target.value as string;
                setViolationFilters({ 
                  ...violationFilters, 
                  resolved: value === '' ? undefined : (value === 'true' ? true : false),
                  page: 1 
                });
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="false">Unresolved</MenuItem>
              <MenuItem value="true">Resolved</MenuItem>
            </Select>
          </FormControl>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => refetchViolations()}
            variant="outlined"
          >
            Refresh
          </Button>
          <Button
            onClick={handleCleanup}
            variant="outlined"
            color="secondary"
          >
            Cleanup Old
          </Button>
        </Box>

        {violationsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : violationsData?.data?.violations ? (
          <>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {violationsData.data.violations.map((violation: any) => (
                    <TableRow key={violation._id}>
                      <TableCell>
                        {new Date(violation.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {violation.userId?.username || violation.userId?.email || violation.userId?._id || 'Unknown'}
                      </TableCell>
                      <TableCell>
                        {getViolationTypeLabel(violation.violationType)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={violation.severity}
                          color={getSeverityColor(violation.severity) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {violation.evidence?.ipDiscrepancy && (
                          <Typography variant="body2">
                            Discrepancy: {violation.evidence.ipDiscrepancy.toFixed(2)}km
                          </Typography>
                        )}
                        {violation.violationType === 'TIMEZONE_MISMATCH' && (
                          <Typography variant="body2" color="textSecondary">
                            Timezone mismatch detected
                          </Typography>
                        )}
                        {violation.metadata?.ip && (
                          <Typography variant="body2" color="textSecondary">
                            IP: {violation.metadata.ip}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {violation.resolved ? (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label="Resolved"
                            color="success"
                            size="small"
                          />
                        ) : (
                          <Chip
                            icon={<WarningIcon />}
                            label="Unresolved"
                            color="warning"
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedViolation(violation);
                                setDetailsDialogOpen(true);
                              }}
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {!violation.resolved && (
                            <Button
                              size="small"
                              onClick={() => {
                                setSelectedViolation(violation);
                                setResolveDialogOpen(true);
                              }}
                            >
                              Resolve
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {violationsData.data.pagination && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2">
                  Page {violationsData.data.pagination.page} of {violationsData.data.pagination.pages} 
                  ({violationsData.data.pagination.total} total)
                </Typography>
                <Box>
                  <Button
                    disabled={violationsData.data.pagination.page === 1}
                    onClick={() => setViolationFilters({ ...violationFilters, page: violationFilters.page - 1 })}
                  >
                    Previous
                  </Button>
                  <Button
                    disabled={violationsData.data.pagination.page >= violationsData.data.pagination.pages}
                    onClick={() => setViolationFilters({ ...violationFilters, page: violationFilters.page + 1 })}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            )}
          </>
        ) : (
          <Alert severity="info">No violations found</Alert>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => refetchHighRisk()}
            variant="outlined"
          >
            Refresh
          </Button>
        </Box>

        {highRiskLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : highRiskUsers?.data && highRiskUsers.data.length > 0 ? (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Violation Count</TableCell>
                  <TableCell>Latest Violation</TableCell>
                  <TableCell>Violation Types</TableCell>
                  <TableCell>Severities</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {highRiskUsers.data.map((user: any) => (
                  <TableRow key={user.userId}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {user.user?.username || user.user?.email || 'Unknown'}
                      </Typography>
                      {user.user?.email && (
                        <Typography variant="caption" color="textSecondary">
                          {user.user.email}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.violationCount}
                        color={user.violationCount >= 5 ? 'error' : 'warning'}
                        icon={<WarningIcon />}
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(user.latestViolation).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {user.violationTypes.map((type: string) => (
                          <Chip
                            key={type}
                            label={getViolationTypeLabel(type)}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {user.severities.map((severity: string) => (
                          <Chip
                            key={severity}
                            label={severity}
                            color={getSeverityColor(severity) as any}
                            size="small"
                          />
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Alert severity="success">
            <CheckCircleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            No high-risk users found. All users are compliant.
          </Alert>
        )}
      </TabPanel>

      <Dialog open={resolveDialogOpen} onClose={() => setResolveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Resolve Violation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Violation Type: {selectedViolation && getViolationTypeLabel(selectedViolation.violationType)}
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            User: {selectedViolation?.userId?.username || selectedViolation?.userId?.email || 'Unknown'}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Resolution Reason"
            fullWidth
            multiline
            rows={4}
            value={resolveReason}
            onChange={(e) => setResolveReason(e.target.value)}
            placeholder="Explain why this violation is being resolved..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleResolveViolation}
            variant="contained"
            disabled={!resolveReason.trim()}
          >
            Resolve
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Violation Details
          <Chip
            label={selectedViolation?.severity}
            color={getSeverityColor(selectedViolation?.severity) as any}
            size="small"
            sx={{ ml: 2 }}
          />
        </DialogTitle>
        <DialogContent>
          {selectedViolation && (
            <Box>
              <Typography variant="h6" gutterBottom sx={{ mt: 1 }}>
                Basic Information
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2"><strong>Type:</strong> {getViolationTypeLabel(selectedViolation.violationType)}</Typography>
                <Typography variant="body2"><strong>Severity:</strong> {selectedViolation.severity}</Typography>
                <Typography variant="body2"><strong>Date:</strong> {new Date(selectedViolation.createdAt).toLocaleString()}</Typography>
                <Typography variant="body2"><strong>Status:</strong> {selectedViolation.resolved ? 'Resolved' : 'Unresolved'}</Typography>
                {selectedViolation.resolved && selectedViolation.resolvedAt && (
                  <Typography variant="body2"><strong>Resolved:</strong> {new Date(selectedViolation.resolvedAt).toLocaleString()}</Typography>
                )}
                {selectedViolation.resolved && selectedViolation.resolvedReason && (
                  <Typography variant="body2"><strong>Resolution Reason:</strong> {selectedViolation.resolvedReason}</Typography>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" gutterBottom>
                User Information
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2"><strong>Username:</strong> {selectedViolation.userId?.username || 'N/A'}</Typography>
                <Typography variant="body2"><strong>Email:</strong> {selectedViolation.userId?.email || 'N/A'}</Typography>
                <Typography variant="body2"><strong>User ID:</strong> {selectedViolation.userId?._id || selectedViolation.userId || 'N/A'}</Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {selectedViolation.violationType === 'TIMEZONE_MISMATCH' && (
                <>
                  <Typography variant="h6" gutterBottom>
                    Timezone Details
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    {selectedViolation.evidence?.deviceTimezone && (
                      <Typography variant="body2">
                        <strong>Device Timezone:</strong> {selectedViolation.evidence.deviceTimezone}
                      </Typography>
                    )}
                    {selectedViolation.evidence?.ipTimezone && (
                      <Typography variant="body2">
                        <strong>IP Timezone:</strong> {selectedViolation.evidence.ipTimezone}
                      </Typography>
                    )}
                    {selectedViolation.evidence?.coordinateTimezone && (
                      <Typography variant="body2">
                        <strong>Coordinate Timezone:</strong> {selectedViolation.evidence.coordinateTimezone}
                      </Typography>
                    )}
                    {!selectedViolation.evidence?.deviceTimezone && !selectedViolation.evidence?.ipTimezone && !selectedViolation.evidence?.coordinateTimezone && (
                      <Typography variant="body2" color="textSecondary">
                        Timezone details not available. This may be due to the IP being localhost (127.0.0.1) which prevents timezone detection.
                      </Typography>
                    )}
                  </Box>
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {selectedViolation.violationType === 'IP_DISCREPANCY' && selectedViolation.evidence?.ipDiscrepancy && (
                <>
                  <Typography variant="h6" gutterBottom>
                    Location Discrepancy
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Distance:</strong> {selectedViolation.evidence.ipDiscrepancy.toFixed(2)} km
                    </Typography>
                    {selectedViolation.evidence?.ipLocation && (
                      <>
                        <Typography variant="body2">
                          <strong>IP Location:</strong> {selectedViolation.evidence.ipLocation.latitude.toFixed(4)}, {selectedViolation.evidence.ipLocation.longitude.toFixed(4)}
                        </Typography>
                        {selectedViolation.evidence.ipLocation.city && (
                          <Typography variant="body2">
                            <strong>IP City:</strong> {selectedViolation.evidence.ipLocation.city}
                          </Typography>
                        )}
                        {selectedViolation.evidence.ipLocation.country && (
                          <Typography variant="body2">
                            <strong>IP Country:</strong> {selectedViolation.evidence.ipLocation.country}
                          </Typography>
                        )}
                      </>
                    )}
                    {selectedViolation.evidence?.userLocation && (
                      <Typography variant="body2">
                        <strong>User GPS Location:</strong> {selectedViolation.evidence.userLocation.latitude.toFixed(4)}, {selectedViolation.evidence.userLocation.longitude.toFixed(4)}
                      </Typography>
                    )}
                  </Box>
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              <Typography variant="h6" gutterBottom>
                Network Information
              </Typography>
              <Box sx={{ mb: 2 }}>
                {selectedViolation.metadata?.ip && (
                  <Typography variant="body2">
                    <strong>IP Address:</strong> {selectedViolation.metadata.ip}
                  </Typography>
                )}
                {selectedViolation.metadata?.userAgent && (
                  <Typography variant="body2">
                    <strong>User Agent:</strong> {selectedViolation.metadata.userAgent}
                  </Typography>
                )}
              </Box>

              {selectedViolation.evidence?.userLocation && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    GPS Coordinates
                  </Typography>
                  <Typography variant="body2">
                    <strong>Latitude:</strong> {selectedViolation.evidence.userLocation.latitude.toFixed(6)}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Longitude:</strong> {selectedViolation.evidence.userLocation.longitude.toFixed(6)}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
          {selectedViolation && !selectedViolation.resolved && (
            <Button
              onClick={() => {
                setDetailsDialogOpen(false);
                setResolveDialogOpen(true);
              }}
              variant="contained"
            >
              Resolve Violation
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Security;
